/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloLink } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { JSX } from "react/jsx-runtime";

import { LIST_INTEGRATIONS_QUERY } from "../../graphql";
import type {
  GetIntegrationQuery,
  GetIntegrationQueryVariables,
  ListIntegrationsQuery,
  ListIntegrationsQueryVariables,
  StartOauthQuery,
  StartOauthQueryVariables,
} from "../../graphql/types";
import {
  GetIntegrationDocument,
  RemoveIntegrationDocument,
  RemoveIntegrationMutation,
  RemoveIntegrationMutationVariables,
  StartOauthDocument,
} from "../../graphql/types";
import { parseGQLResponseError, parsePagedEntries } from "../../graphql/util";
import { useNavigator } from "../../lib/Hooks";
import { getObjectField, NestedObject } from "../../lib/Util";
import { HIDDEN_SLACK_INTEGRATION, SLACK_INTEGRATION } from "./SlackIntegration";
import { TEST_INTEGRATION } from "./TestIntegration";
import { UNKNOWN_INTEGRATION } from "./UnknownIntegration";

type IntegrationMetadata = {
  image: string;
  title: string;
  description: JSX.Element;
  integrationType: string;
  oauth: boolean;
};

// Listed in the order to be presented to the user when choosing a new integration.
const INTEGRATION_TYPES: IntegrationMetadata[] = [SLACK_INTEGRATION, HIDDEN_SLACK_INTEGRATION, TEST_INTEGRATION];

const INTEGRATION_TYPE_MAP = (() => {
  const byType: { [key: string]: any } = {};
  INTEGRATION_TYPES.forEach((integration) => (byType[integration.integrationType] = integration));
  return byType;
})();

const Integration = (node: any) => {
  const t = INTEGRATION_TYPE_MAP[node.integration_type];
  const meta = t === undefined ? UNKNOWN_INTEGRATION : t;
  return new meta.clazz(node, meta);
};

const augment = (prototype: any, augmentation: any) => {
  return Object.freeze(Object.assign({}, prototype, augmentation));
};

const useGetIntegration = (id: string) => {
  const baseQuery = useQuery<GetIntegrationQuery, GetIntegrationQueryVariables>(GetIntegrationDocument, {
    variables: { id: id },
  });

  const data = getObjectField(baseQuery as unknown as NestedObject, "data.integration");
  const integration = data ? Integration(data) : null;

  const query = augment(baseQuery, { integration });

  return query;
};

const useListIntegrations = () => {
  const query = useQuery<ListIntegrationsQuery, ListIntegrationsQueryVariables>(LIST_INTEGRATIONS_QUERY);
  const parsed = parsePagedEntries(query.data, "integrations", Integration);
  return augment(query, parsed);
};

const useRemoveIntegration = (): [
  (id: string) => Promise<ApolloLink.Result<RemoveIntegrationMutation>>,
  useMutation.Result<RemoveIntegrationMutation>,
] => {
  const onError = () => {
    toast.error("The integration could not be removed.");
  };

  const onCompleted = () => {
    toast.success("The integration was removed.");
  };

  const [baseMutator, baseMutation] = useMutation<RemoveIntegrationMutation, RemoveIntegrationMutationVariables>(
    RemoveIntegrationDocument,
    {
      onError: onError,
      onCompleted: onCompleted,
      refetchQueries: ["list_integrations"],
    }
  );

  const mutator = (id: string) => {
    return baseMutator({
      variables: {
        id: id,
      },
    });
  };

  return [mutator, baseMutation];
};

const useStartOAuth = (entity = "integration", type: string, id: string | null = null) => {
  const baseQuery = useQuery<StartOauthQuery, StartOauthQueryVariables>(StartOauthDocument, {
    variables: {
      entity,
      oauthType: type,
      id,
    },
  });

  const errorMessage = baseQuery.error ? parseGQLResponseError(baseQuery.error) !== null : false;

  useEffect(() => {
    if (baseQuery.data?.start_oauth) {
      window.open(baseQuery.data.start_oauth, "oauthpop", "width=800, height=600");
    }
  }, [baseQuery.data?.start_oauth]);

  const query = Object.freeze(Object.assign({}, baseQuery, { errorMessage }));

  return query;
};

const useDismiss = () => {
  const navigator = useNavigator();

  const dismiss = () => {
    navigator.goto("integrations");
  };

  return dismiss;
};

export {
  Integration,
  INTEGRATION_TYPES,
  useDismiss,
  useGetIntegration,
  useListIntegrations,
  useRemoveIntegration,
  useStartOAuth,
};
export type { IntegrationMetadata };
